import React, { useEffect, useMemo, useState } from 'react'
import {
  CustomLocation,
  CustomLocationsConfig,
  RestaurantInfo
} from '../../../types/config'
import { Select, SelectItemContainer } from '@toasttab/buffet-pui-select'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import { AddIcon } from '@toasttab/buffet-pui-icons'
import { FulfillmentFormValues } from '../../../types/fulfillment'
import { DiningOptionBehavior } from '../../../types/cart'
import {
  SavedAddressQuery,
  useCustomerAuth
} from '../../../auth/CustomerAuthContext'
import { DeliveryInfo } from '../../../types/orders'
import {
  addressToDeliveryInfo,
  addressToString,
  customerAddressToDeliveryInfo
} from '../../../utils/address-helpers'

type LocationOptionType =
  | 'customerAddress'
  | 'customLocation'
  | 'standardTakeout'
  | 'standardDelivery'

type LocationOption = {
  label: string
  subLabel?: string
  type: LocationOptionType
  address?: DeliveryInfo
  behavior?: DiningOptionBehavior
}

type IndexedLocationOption = LocationOption & {
  value: number
}

export const getDefaultCustomLocation = (
  customLocationConfig: CustomLocationsConfig | undefined,
  behavior: DiningOptionBehavior | undefined
): CustomLocation | undefined => {
  if (!customLocationConfig || !behavior) {
    return undefined
  }

  if (
    (behavior === 'TAKE_OUT' && !customLocationConfig.standardTakeout) ||
    (behavior === 'DELIVERY' && !customLocationConfig.standardDelivery)
  ) {
    const byBehavior = customLocationConfig.locations.filter(
      (b) => b.behavior === behavior
    )

    if (byBehavior.length === 1) {
      return byBehavior[0]
    }
  }

  return undefined
}

const getIndexedOptions = (
  customLocations: CustomLocation[],
  customerAddresses: SavedAddressQuery[],
  restaurantInfo: RestaurantInfo | undefined,
  supportStandardTakeout: boolean,
  supportStandardDelivery: boolean
): IndexedLocationOption[] => {
  const options: LocationOption[] = customLocations.map((location) => {
    return {
      label: location.name,
      subLabel: addressToString(location.address),
      type: 'customLocation',
      address: addressToDeliveryInfo(location.address),
      behavior: location.behavior
    }
  })

  if (supportStandardTakeout) {
    options.unshift({
      label: restaurantInfo!!.name ?? restaurantInfo!!.locationName,
      subLabel: addressToString(restaurantInfo!!.address),
      type: 'standardTakeout',
      behavior: 'TAKE_OUT'
    })
  }

  if (supportStandardDelivery) {
    const addressOptions: LocationOption[] = customerAddresses.map(
      (customerAddress) => {
        const address = customerAddressToDeliveryInfo(customerAddress)
        const name = customerAddress.name || ''

        return {
          label: addressToString(address)!!,
          subLabel: name,
          type: 'customerAddress',
          address: address,
          name: name,
          behavior: 'DELIVERY'
        }
      }
    )

    options.push(...addressOptions)

    options.unshift({
      label: 'Enter delivery address',
      type: 'standardDelivery',
      behavior: 'DELIVERY'
    })
  }

  return options.map((option, index) => ({ ...option, value: index }))
}

const getValueByAddress = (
  indexedOptions: IndexedLocationOption[],
  address: DeliveryInfo | undefined,
  supportStandardDelivery: boolean,
  standardDeliveryIndex: number
) => {
  if (!address) return undefined

  const index = indexedOptions.findIndex((option) => {
    if (!option.address) return false

    return addressToString(address) === addressToString(option.address)
  })

  if (index >= 0) {
    return index
  } else if (supportStandardDelivery) {
    return standardDeliveryIndex
  } else {
    return undefined
  }
}

const getValueByCustomLocation = (
  indexedOptions: IndexedLocationOption[],
  customLocationName: string | undefined,
  supportStandardTakeout: boolean,
  standardFulfillment: boolean
) => {
  if (customLocationName) {
    const index = indexedOptions.findIndex((option) => {
      return (
        option.type === 'customLocation' && option.label === customLocationName
      )
    })

    return index >= 0 ? index : undefined
  } else if (supportStandardTakeout && standardFulfillment) {
    return indexedOptions.find((option) => option.type == 'standardTakeout')
      ?.value
  }

  return undefined
}

interface Props {
  customLocationsConfig: CustomLocationsConfig | undefined
  values: FulfillmentFormValues
  setValues: (values: FulfillmentFormValues) => void
  disabled?: boolean
  setHideAddress: (hide: boolean) => void
}

export const PrefilledLocationPicker = ({
  customLocationsConfig,
  values,
  setValues,
  disabled,
  setHideAddress
}: Props) => {
  const { restaurantInfo } = useRestaurant()

  const [prefilledLocationValue, setPrefilledLocationValue] = useState<number>()

  const {
    standardFulfillment,
    diningOptionBehavior,
    customLocationName,
    deliveryInfo
  } = values

  const customLocations = useMemo(() => {
    return (
      customLocationsConfig?.locations.filter(
        (location) => location.behavior === diningOptionBehavior
      ) || []
    )
  }, [customLocationsConfig, diningOptionBehavior])

  const supportStandardTakeout =
    diningOptionBehavior === 'TAKE_OUT' &&
    customLocationsConfig?.standardTakeout &&
    (restaurantInfo?.name ?? restaurantInfo?.locationName)

  const supportStandardDelivery =
    diningOptionBehavior === 'DELIVERY' &&
    (customLocationsConfig?.standardDelivery || !customLocationsConfig)

  const { validDeliveryAddresses } = useCustomerAuth()

  const indexedOptions = useMemo(() => {
    return getIndexedOptions(
      customLocations,
      validDeliveryAddresses,
      restaurantInfo,
      !!supportStandardTakeout,
      supportStandardDelivery
    )
  }, [
    customLocations,
    validDeliveryAddresses,
    restaurantInfo,
    supportStandardTakeout,
    supportStandardDelivery
  ])

  const standardDeliveryIndex = indexedOptions.findIndex(
    (option) => option.type === 'standardDelivery'
  )

  const nonStandardOptions = indexedOptions.filter(
    (option) =>
      option.type !== 'standardDelivery' && option.type !== 'standardTakeout'
  )

  const standardIsOnlyOption = !nonStandardOptions.length

  useEffect(() => {
    const initialValue =
      diningOptionBehavior === 'DELIVERY'
        ? getValueByAddress(
            indexedOptions,
            deliveryInfo,
            supportStandardDelivery,
            standardDeliveryIndex
          )
        : getValueByCustomLocation(
            indexedOptions,
            customLocationName,
            !!supportStandardTakeout,
            !!standardFulfillment
          )
    setPrefilledLocationValue(initialValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diningOptionBehavior])

  useEffect(() => {
    const standardSelected = prefilledLocationValue === standardDeliveryIndex

    const showAddressPicker =
      diningOptionBehavior === 'DELIVERY' &&
      supportStandardDelivery &&
      (standardSelected || standardIsOnlyOption)

    setHideAddress(!showAddressPicker)
  }, [
    diningOptionBehavior,
    supportStandardDelivery,
    prefilledLocationValue,
    standardDeliveryIndex,
    standardIsOnlyOption
  ])

  if (!diningOptionBehavior || standardIsOnlyOption) {
    return null
  }

  const handleChange = (index: number) => {
    setPrefilledLocationValue(index)

    const option = indexedOptions[index]

    switch (option.type) {
      case 'standardTakeout':
      case 'standardDelivery': {
        setHideAddress(option.type !== 'standardDelivery')
        setValues({
          ...values,
          customLocationName: undefined,
          standardFulfillment: true,
          deliveryInfo: undefined
        })
        break
      }
      case 'customLocation': {
        setHideAddress(true)
        setValues({
          ...values,
          customLocationName: option.label,
          standardFulfillment: false,
          deliveryInfo:
            option.behavior === 'DELIVERY' ? option.address : undefined
        })
        break
      }
      case 'customerAddress': {
        setHideAddress(true)
        setValues({
          ...values,
          customLocationName: undefined,
          standardFulfillment: true,
          deliveryInfo: option.address
        })
        break
      }
      default:
        break
    }
  }

  return (
    <Select
      disabled={disabled}
      testId='PrefilledLocationSelect'
      containerClassName='py-4'
      label={
        diningOptionBehavior === 'TAKE_OUT' ? 'Pickup from' : 'Delivery to'
      }
      placeholder={'Select one'}
      value={prefilledLocationValue}
      onChange={handleChange}
      renderItem={(option) => (
        <SelectItemContainer {...option}>
          {option.item.value === -1 && diningOptionBehavior === 'DELIVERY' ? (
            <div className='text-link font-semibold'>
              <AddIcon accessibility='decorative' />
              {option.item.label}
            </div>
          ) : (
            <div>
              {option.item.label}
              {option.item.subLabel && (
                <div className={'text-secondary type-subhead'}>
                  {option.item.subLabel}
                </div>
              )}
            </div>
          )}
        </SelectItemContainer>
      )}
      options={indexedOptions}
    />
  )
}
